import * as Effects from 'redux-saga/effects';
import { CONSTANTS } from './constants';
import * as apiService from './service_debtProducts';
import { JAVA_API_STATUS, SOMETHING_WRONG } from '../../utils/axios-interceptor';
import { toast } from 'react-toastify';

const call: any = Effects.call;
const put = Effects.put;
const takeEvery = Effects.takeEvery;

interface ProductMetricResponse {
    productId: string;
    lenderId: string;
    probability: number;
    amount: string;
    tenure: string;
    roi: string;
    details: any;
    security?: any;
    originalAmount?: string;
    originalTenure?: string;
    originalRoi?: string;
    metricSourceMap?: {
        LIMIT_METRIC?: string;
        ROI_METRIC?: string;
        SECURITY_METRIC?: string;
        TENURE_METRIC?: string;
    };
}

/**
 * Updates Redux state with the response from generateProductMetrics API
 * If aggregatorId === lenderId, product is updated; otherwise lenders are updated
 */
function* updateReduxFromResponse(responseData: ProductMetricResponse[], aggregatorId: string) {
    if (!responseData || !Array.isArray(responseData)) return;

    const productUpdates: Array<{ productId: string; updates: any }> = [];
    const lenderUpdates: Array<{ lenderId: string; productId: string; updates: any }> = [];

    responseData.forEach((item) => {
        // If aggregatorId and lenderId are the same, it's a product update
        if (item.lenderId === aggregatorId) {
            productUpdates.push({
                productId: item.productId,
                updates: {
                    probability: String(item.probability),
                    amount: item.amount,
                    tenure: item.tenure,
                    roi: item.roi,
                    details: item.details,
                    security: item.security,
                    originalAmount: item.originalAmount,
                    originalTenure: item.originalTenure,
                    originalRoi: item.originalRoi,
                    metricSourceMap: item.metricSourceMap,
                },
            });
        } else {
            // Otherwise, it's a lender update
            lenderUpdates.push({
                lenderId: item.lenderId,
                productId: item.productId,
                updates: {
                    probability: String(item.probability),
                    amount: item.amount,
                    tenure: item.tenure,
                    roi: item.roi,
                    details: item.details,
                    security: item.security,
                    originalAmount: item.originalAmount,
                    originalTenure: item.originalTenure,
                    originalRoi: item.originalRoi,
                    metricSourceMap: item.metricSourceMap,
                },
            });
        }
    });

    // Dispatch batch updates
    if (productUpdates.length > 0) {
        yield put({
            type: CONSTANTS.BATCH_UPDATE_PRODUCTS,
            payload: productUpdates,
        });
    }

    if (lenderUpdates.length > 0) {
        yield put({
            type: CONSTANTS.BATCH_UPDATE_LENDERS,
            payload: lenderUpdates,
        });
    }
}

/**
 * Saga to generate product metrics
 * Calls API with provided payload and updates Redux with response
 * Business logic for splitting calls should be handled in the component layer
 */
function* generateProductMetricsSaga(action: {
    type: string;
    payload: {
        aggregatorId: string;
        orgId: string;
        productTerms: Record<string, Record<string, any>>;
    };
    handlers?: {
        onSuccess?: (data: any) => void;
        onError?: (error?: any) => void;
    };
}) {
    try {
        const { aggregatorId } = action.payload;
        const response = yield call(apiService.generateProductMetrics, action.payload);
        const responseData = response?.data?.responseData;

        switch (responseData && responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS: {
                const data = response?.data?.responseBody?.data;

                // Update Redux state with the response
                yield* updateReduxFromResponse(data, aggregatorId);

                // Dispatch success action
                yield put({
                    type: CONSTANTS.GENERATE_PRODUCT_METRICS_SUCCESS,
                    payload: data,
                });

                // Call success handler with the response data
                action.handlers?.onSuccess?.(data);
                break;
            }
            case JAVA_API_STATUS.ERROR:
            default:
                toast.error(responseData?.responseMessage || SOMETHING_WRONG);
                yield put({
                    type: CONSTANTS.GENERATE_PRODUCT_METRICS_FAILURE,
                    payload: responseData?.responseMessage,
                });
                action.handlers?.onError?.(responseData?.responseMessage);
                break;
        }
    } catch (err: any) {
        console.error('Error in generateProductMetrics saga:', err?.message || 'Some error occurred');
        yield put({
            type: CONSTANTS.GENERATE_PRODUCT_METRICS_FAILURE,
            payload: err?.message,
        });
        action.handlers?.onError?.(err);
    }
}

export default function* debtProductsSagaWatcher() {
    yield takeEvery(CONSTANTS.GENERATE_PRODUCT_METRICS, generateProductMetricsSaga);
}
